<template>
  <LoadingDiv v-if="!isReady" />

  <div v-else class="user-settings-shell">
    <div class="user-toolbar">
      <div>
        <span class="user-eyebrow">Access Control</span>
        <h5>User Accounts</h5>
        <p>Manage HMO staff access, authority level, scheme visibility, and module permissions.</p>
      </div>

      <button
        type="button"
        class="user-primary-action"
        :class="{ 'is-cancel': next }"
        @click="handleSwitch"
      >
        <i class="material-icons">{{ next ? "close" : "add" }}</i>
        {{ next ? "Cancel" : "Add User" }}
      </button>
    </div>

    <template v-if="!next">
      <div class="user-highlight-card" v-if="newUser">
        <div class="user-avatar">
          <img :src="newUser.profile_img" v-if="newUser.profile_img" />
          <img :src="`https://ui-avatars.com/api/?name=${newUser?.name}&background=a0a0a0&size=128`" v-else />
        </div>
        <div>
          <span class="user-eyebrow">Recent update</span>
          <h6>{{ newUser.name }}</h6>
          <p>
            {{ newUser.email }}
            <span v-if="newPasses.find(x => x.id === newUser.id)">
              · Password:
              <strong>{{ newPasses.find(x => x.id === newUser.id)?.password }}</strong>
            </span>
          </p>
        </div>
        <span class="user-status-pill" :class="`is-${newUser.status}`">
          {{ newUser.status === "a" ? "Active" : newUser.status === "p" ? "Awaiting Login" : "Deactivated" }}
        </span>
      </div>

      <div class="user-card-grid" v-if="users.length">
        <article
          v-for="(user, id) in users"
          :key="user.id"
          class="user-account-card"
          :class="`is-${user.status}`"
        >
          <div class="user-card-index">{{ id + 1 }}</div>

          <div class="user-card-main">
            <div class="user-card-head">
              <div class="user-avatar">
                <img :src="user.profile_img" v-if="user.profile_img" />
                <img :src="`https://ui-avatars.com/api/?name=${user?.name}&background=a0a0a0&size=128`" v-else />
              </div>
              <div>
                <h6>{{ user.name }}</h6>
                <p>{{ user.designation || "No designation set" }}</p>
              </div>
            </div>

            <div class="user-meta-grid">
              <div>
                <small>Username</small>
                <strong>{{ user.email }}</strong>
              </div>
              <div>
                <small>Status</small>
                <span class="user-status-pill" :class="`is-${user.status}`">
                  {{ user.status === "a" ? "Active" : user.status === "p" ? "Awaiting Login" : "Deactivated" }}
                </span>
              </div>
            </div>

            <div class="user-password-note" v-if="newPasses.find(x => x.id === user.id)">
              Reset password:
              <strong>{{ newPasses.find(x => x.id === user.id)?.password }}</strong>
            </div>

            <div class="user-access-summary">
              <span>Level {{ user.level || "-" }}/5</span>
              <span>{{ user.schemes?.length || 0 }} scheme access</span>
              <span>{{ user.modules?.length || 0 }} module access</span>
            </div>

            <button
              type="button"
              class="user-expand-toggle"
              @click="toggleUserDetails(user.id)"
            >
              <i class="material-icons">
                {{ isUserExpanded(user.id) ? "expand_less" : "expand_more" }}
              </i>
              {{ isUserExpanded(user.id) ? "Hide details" : "Show details" }}
            </button>

            <div class="user-detail-drawer" v-if="isUserExpanded(user.id)">
              <div>
                <small>Department</small>
                <strong>{{ user.department || "Not set" }}</strong>
              </div>
              <div>
                <small>Staff ID</small>
                <strong>{{ user.staff_id || "Not set" }}</strong>
              </div>
              <div>
                <small>Official Line</small>
                <strong>{{ user.official_line || "Not set" }}</strong>
              </div>
              <div>
                <small>Authority Level</small>
                <strong>Level {{ user.level || "-" }}/5</strong>
              </div>
              <div class="user-detail-wide">
                <small>Schemes</small>
                <p>
                  <span v-for="scheme in user.schemes" :key="scheme" class="user-detail-chip">
                    {{ allSchemes?.find(x => x.id === scheme)?.name || "Scheme" }}
                  </span>
                  <span v-if="!user.schemes?.length" class="user-detail-muted">No scheme access set</span>
                </p>
              </div>
              <div class="user-detail-wide">
                <small>Modules</small>
                <p>
                  <span v-for="module in user.modules" :key="module" class="user-detail-chip">
                    {{ module }}
                  </span>
                  <span v-if="!user.modules?.length" class="user-detail-muted">No module access set</span>
                </p>
              </div>
            </div>
          </div>

          <div class="user-card-actions">
            <LoadingSmallDiv v-if="isLoading === user.id" />
            <template v-else>
              <button v-if="user.status !== 'd'" type="button" title="Edit user" @click="handleEdit(user)">
                <i class="material-icons">edit</i>
              </button>
              <button
                type="button"
                :title="user.status == 'a' || user.status == 'p' ? 'Suspend user' : 'Reactivate user'"
                @click="handleStatus(user)"
              >
                <i class="material-icons">
                  {{ user.status == "a" || user.status == "p" ? "pause_circle_outline" : "play_circle_filled" }}
                </i>
              </button>
              <button v-if="user.status !== 'd'" type="button" title="Reset user" @click="handleReset(user)">
                <i class="material-icons">sync</i>
              </button>
            </template>
          </div>
        </article>
      </div>

      <div class="user-empty-card" v-else>
        <i class="material-icons">person_off</i>
        <div>
          <h6>No users found</h6>
          <p>Add a user account to give HMO staff controlled access to the admin workspace.</p>
        </div>
      </div>

      <div class="user-footer-action">
        <button type="button" class="waves-effect waves-light btn-large" @click="handleNext">
          <i class="material-icons right">call_made</i>
          Next
        </button>
      </div>
    </template>

    <form v-else class="user-editor-form" novalidate @submit.prevent="handleAdd">
      <section class="user-editor-section">
        <div class="user-editor-head">
          <span class="user-eyebrow">{{ userID ? "Edit Account" : "New Account" }}</span>
          <h5>{{ userID ? "Update User" : "New HMO User" }}</h5>
          <p>Set the login identity and staff details for this administrative user.</p>
        </div>

        <div class="user-field-grid">
          <div class="user-field-card">
            <div class="input-field">
              <input type="text" id="userName" v-model="userName" required />
              <label :class="{ active: userID }" for="userName">Staff Name</label>
            </div>
          </div>
          <div class="user-field-card">
            <div class="input-field">
              <input type="text" id="userDesignation" v-model="userDesignation" required />
              <label :class="{ active: userID }" for="userDesignation">Staff Designation</label>
            </div>
          </div>
          <div class="user-field-card user-field-wide">
            <div class="input-field">
              <input type="text" id="userEmail" v-model="userEmail" required />
              <label :class="{ active: userID }" for="userEmail">Login Email Address</label>
            </div>
          </div>
          <div class="user-field-card">
            <div class="input-field">
              <input type="text" id="userDepartment" v-model="userDepartment" />
              <label :class="{ active: userID }" for="userDepartment">Staff Department</label>
            </div>
          </div>
          <div class="user-field-card">
            <div class="input-field">
              <input type="text" id="userStaffID" v-model="userStaffID" />
              <label :class="{ active: userID }" for="userStaffID">Company Staff ID</label>
            </div>
          </div>
          <div class="user-field-card user-field-wide">
            <div class="input-field">
              <input type="text" id="userOfficialLine" v-model="userOfficialLine" />
              <label :class="{ active: userID }" for="userOfficialLine">Staff Official Line</label>
            </div>
          </div>
        </div>
      </section>

      <section class="user-editor-section">
        <div class="user-editor-head">
          <span class="user-eyebrow">Permissions</span>
          <h5>Authority & Access</h5>
          <p>Choose the authority level, schemes, and application modules this user can work with.</p>
        </div>

        <div class="user-field-grid">
          <div class="user-field-card user-field-wide">
            <div class="input-field">
              <select id="userLevel" v-model="userLevel" required>
                <option value="" disabled selected>Choose your option</option>
                <option value="1">Intern</option>
                <option value="3">Staff</option>
                <option value="5">Manager</option>
              </select>
              <label for="userLevel">User Authority Level</label>
            </div>
          </div>

          <div class="user-check-card">
            <h6>Scheme Access</h6>
            <label v-for="scheme in allSchemes" :key="scheme.id">
              <input type="checkbox" class="filled-in" :value="scheme.id" v-model="userSchemes" />
              <span>{{ scheme.name }}</span>
            </label>
          </div>

          <div class="user-check-card">
            <h6>Module Access</h6>
            <label v-for="module in allModules" :key="module.id">
              <input type="checkbox" class="filled-in" :value="module.to" v-model="userModules" />
              <span>{{ module.title }} <em>{{ module.description }}</em></span>
            </label>
          </div>
        </div>
      </section>

      <div class="user-form-error" v-if="userFormErrors.length">
        <strong>Please fix this before saving.</strong>
        <ul>
          <li v-for="message in userFormErrors" :key="message">{{ message }}</li>
        </ul>
      </div>

      <div class="user-form-actions">
        <button type="button" class="btn-flat waves-effect" :class="{ disabled: isSubmitting }" @click="handleBack">
          Cancel
        </button>
        <button class="waves-effect waves-light btn-large" :class="{ disabled: isSubmitting }">
          <i class="material-icons right">check_circle</i>
          {{ isSubmitting ? "Sending..." : "Save User" }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUpdated } from "vue";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import putItem from "@/composables/putItem";
import LoadingDiv from "@/components/LoadingDiv.vue";
import LoadingSmallDiv from "@/components/LoadingSmallDiv.vue";
import M from "materialize-css";

export default {
  name: "UserSetupComponent",
  components: { LoadingDiv, LoadingSmallDiv },
  emits: ["next"],
  setup(props, context) {
    const response = ref(null);
    const allSchemes = ref([]);
    const allModules = ref([]);
    const newPasses = ref([]);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const userID = ref(null);
    const userStatus = ref(null);
    const userName = ref(null);
    const userDesignation = ref(null);
    const userEmail = ref(null);
    const userDepartment = ref(null);
    const userStaffID = ref(null);
    const userOfficialLine = ref(null);
    const userLevel = ref(null);
    const userSchemes = ref([]);
    const userModules = ref([]);
    const newUser = ref(null);
    const next = ref(false);
    const expandedUsers = ref([]);

    // store
    const store = useStore();

    // select dropdown
    onMounted(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });

    // select dropdown
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      M.FormSelect.init(elemsSelect, {});
    });


    // start
    onMounted(() => {
      // get items
      getUsers();
    });

    // get users
    const getUsers = (newUserId) => {
      // loasder on
      isReady.value = false;
      // start
      (async () => {
        const { res, err } = await getItems("authority/user/all");

        if (res.value) {
          response.value = res.value.data;
          allSchemes.value = res.value.schemes;
          allModules.value = (res.value.modules || []).map((module) => ({
            ...module,
            to: module.module_key,
          }));
          newUser.value = newUserId ? res.value.data.find(x => x.id === newUserId) : null; // clear new user on reload
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
        next.value = false;
      })();
    }

    // clear user
    const clearUser = () => {
      // clear all  
      userID.value = null;
      userStatus.value = null;
      userName.value = null;
      userDesignation.value = null;
      userEmail.value = null;
      userDepartment.value = null;
      userStaffID.value = null;
      userOfficialLine.value = null;
      userLevel.value = null;
      userSchemes.value = [];
      userModules.value = [];
    }

    // set user
    const setUser = (val) => {
      // set all  
      userID.value = val.id;
      userStatus.value = val.status;
      userName.value = val.name;
      userDesignation.value = val.designation;
      userEmail.value = val.email;
      userDepartment.value = val.department;
      userStaffID.value = val.staff_id;
      userOfficialLine.value = val.official_line;
      userLevel.value = val.level;
      userSchemes.value = val?.schemes?.length ? val.schemes : [];
      userModules.value = val?.modules?.length
        ? val.modules.filter((module) => allModules.value.some((item) => item.to === module))
        : [];
    }

    // edit User
    const handleEdit = (val) => {
      if (val.status !== 'd') {
        error.value = null;
        setUser(val);
        next.value = true;
      } else {
        // get all
        getUsers();
      }
    };

    // edit status
    const handleStatus = async (val) => {

      // start
      isLoading.value = val.id;

      // new
      let newStatus = null;

      // new status
      if (val.status == "a" || val.status == "p") {
        newStatus = "d"; // deactivate account
      } else if (val.status == "d") {
        newStatus = "p"; // make pending
      }

      // set new password (all the time)
      let newPass = Array(7).fill().map(() => String.fromCharCode(Math.floor(Math.random() * 26) + 97)).join('');

      // update
      let newVal = {
        status: newStatus,
        password: newPass,
      };

      // post item
      const { res, err } = await putItem("authority/hmo/update/vanilla", val.id, newVal);

      if (res.value) {
        // keep new password
        if (res.value.data.status !== "d") {
          // filter old passes out
          newPasses.value = newPasses.value.filter(x => x.id !== res.value.data.id);
          // keep password
          newPasses.value.push({ id: res.value.data.id, password: newPass });
        }
        // clear User
        clearUser();
        // reload users
        getUsers(res.value.data.id);
      } else {
        error.value = err.value;
      }

      // end
      isLoading.value = false;
    };

    // edit User status
    const handleReset = async (val) => {

      // start
      isLoading.value = val.id;

      if (val.status !== 'd') {

        // new
        let newStatus = "p";
        // set new password (all the time)
        let newPass = Array(7).fill().map(() => String.fromCharCode(Math.floor(Math.random() * 26) + 97)).join('');

        // update
        let newVal = {
          status: newStatus,
          password: newPass,
        };

        // post item
        const { res, err } = await putItem("authority/hmo/update/vanilla", val.id, newVal);

        if (res.value) {
          // filter old passes out
          newPasses.value = newPasses.value.filter(x => x.id !== res.value.data.id);
          // keep new password
          newPasses.value.push({ id: res.value.data.id, password: newPass });
          // clear User
          clearUser();
          // reload users
          getUsers(res.value.data.id);
        } else {
          error.value = err.value;
        }

      } else {
        // get all
        getUsers();
      }

      // end
      isLoading.value = false;
    };

    // handle update complete
    const handleUpdateComplete = (val) => {
      console.log(val);
    };

    const handleBack = () => {
      error.value = null;
      // restart
      next.value = false;
      // clear user
      clearUser();
    };

    const handleSwitch = () => {
      error.value = null;

      if (next.value) {
        // switch to main view
        next.value = false;
        // safety rule
        clearUser();
      } else {
        // switch to add/edit
        next.value = true;
      }
    };

    const isUserExpanded = (id) => {
      return expandedUsers.value.includes(id);
    };

    const toggleUserDetails = (id) => {
      if (isUserExpanded(id)) {
        expandedUsers.value = expandedUsers.value.filter((x) => x !== id);
      } else {
        expandedUsers.value.push(id);
      }
    };

    const normalizeEmail = (value) => {
      return String(value || "").trim().toLowerCase();
    };

    const isValidEmail = (value) => {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
    };

    const userExistsWithEmail = (value) => {
      const email = normalizeEmail(value);

      if (!email) {
        return false;
      }

      return users.value.some((user) => {
        return (
          normalizeEmail(user.email) === email &&
          Number(user.id) !== Number(userID.value)
        );
      });
    };

    const requiredUserErrors = () => {
      const messages = [];

      if (!String(userName.value || "").trim()) {
        messages.push("Staff name is required.");
      }

      if (!String(userDesignation.value || "").trim()) {
        messages.push("Staff designation is required.");
      }

      if (!String(userEmail.value || "").trim()) {
        messages.push("Login email address is required.");
      } else if (!isValidEmail(userEmail.value)) {
        messages.push("Enter a valid login email address.");
      }

      if (!userLevel.value) {
        messages.push("User authority level is required.");
      }

      if (userExistsWithEmail(userEmail.value)) {
        messages.push("A user with this login email already exists. Use another email or edit the existing user.");
      }

      return messages;
    };

    const normalizeServerError = (value) => {
      const message = String(value || "").trim();
      const lowerMessage = message.toLowerCase();

      if (
        lowerMessage.includes("duplicate entry") ||
        lowerMessage.includes("users_email_unique") ||
        lowerMessage.includes("email has already been taken")
      ) {
        return "A user with this login email already exists. Use another email or edit the existing user.";
      }

      if (lowerMessage.includes("unauthorized")) {
        return "Your session has expired. Please sign in again before saving this user.";
      }

      if (lowerMessage.includes("forbidden")) {
        return "You do not have permission to save this user.";
      }

      if (lowerMessage.includes("server error")) {
        return "The server could not save this user. If the email already belongs to another account, use another email and try again.";
      }

      return message || "The user could not be saved. Please check the details and try again.";
    };

    const setServerError = (value) => {
      error.value = [normalizeServerError(value)];
    };

    // handle add
    const handleAdd = async () => {
      if (isSubmitting.value) {
        return;
      }

      const validationErrors = requiredUserErrors();

      if (!validationErrors.length) {

        // Benefits
        // Start Uploading
        isSubmitting.value = true;
        error.value = null;

        // round up
        const newVal = {
          name: userName.value?.trim(),
          slug: userName.value?.trim().split(' ')[0],
          designation: userDesignation.value?.trim(),
          email: userEmail.value?.trim(),
          department: userDepartment.value,
          staff_id: userStaffID.value,
          official_line: userOfficialLine.value,
          level: userLevel.value,
          schemes: userSchemes.value,
          modules: userModules.value.filter((module) =>
            allModules.value.some((item) => item.to === module)
          ),
          hmo_id: store.state.user.hmo_id,
        };

        if (userID.value && userStatus.value && userStatus.value !== "d") {
          // update User account
          const { res, err } = await putItem("authority/hmo/update/vanilla", userID.value, newVal);

          if (res.value) {
            // clear
            clearUser();
            // get all
            getUsers(res.value.data.id);
          } else {
            setServerError(err.value);
            isSubmitting.value = false;
          }

        } else if (!userID.value) {

          // pass
          let newPass = Array(7).fill().map(() => String.fromCharCode(Math.floor(Math.random() * 26) + 97)).join('');

          // prime object
          newVal.type = "hmo";
          newVal.status = "p";
          newVal.password = newPass;

          // creat new User account
          const { res, err } = await postItem("authority/hmo/create", newVal);

          if (res.value) {
            // filter old passes out
            newPasses.value = newPasses.value.filter(x => x.id !== res.value.data.id);
            // keep new password
            newPasses.value.push({ id: res.value.data.id, password: newPass });
            // clear
            clearUser();
            // get all
            getUsers(res.value.data.id);
          } else {
            setServerError(err.value);
          }

        }

        isSubmitting.value = false;

      } else {
        error.value = validationErrors;
      }
    };

    // handle submit
    const handleNext = () => {
      // next
      // emit new parameters
      context.emit("next", {
        step: 3,
        isComplete: true,
      });
    };

    // output results (plans)
    const users = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const userFormErrors = computed(() => {
      if (!error.value || error.value === false || !next.value) {
        return [];
      }

      if (Array.isArray(error.value)) {
        return error.value.filter(Boolean);
      }

      if (error.value === true) {
        return ["Staff name, designation, login email, and authority level are required."];
      }

      return [normalizeServerError(error.value)];
    });

    return {
      isReady,
      isLoading,
      isSubmitting,
      error,
      userFormErrors,
      handleStatus,
      handleReset,
      handleEdit,
      handleNext,
      handleAdd,
      handleBack,
      handleSwitch,
      isUserExpanded,
      toggleUserDetails,
      handleUpdateComplete,
      userID,
      userName,
      userDesignation,
      userEmail,
      userDepartment,
      userStaffID,
      userOfficialLine,
      userLevel,
      userSchemes,
      userModules,
      next,
      allModules,
      allSchemes,
      newPasses,
      newUser,
      users,
    };
  },
};
</script>

<style scoped>
.user-settings-shell {
  display: grid;
  gap: 18px;
}

.user-toolbar,
.user-editor-section,
.user-account-card,
.user-highlight-card,
.user-empty-card {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
}

.user-toolbar {
  align-items: center;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 22px 24px;
}

.user-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.user-toolbar h5,
.user-editor-head h5,
.user-empty-card h6,
.user-highlight-card h6,
.user-card-head h6 {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.user-toolbar h5,
.user-editor-head h5 {
  font-size: 1.34rem;
}

.user-toolbar p,
.user-editor-head p,
.user-empty-card p,
.user-highlight-card p,
.user-card-head p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px 0 0;
}

.user-primary-action {
  align-items: center;
  background: #00897b;
  border: 0;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  font-weight: 800;
  gap: 8px;
  min-height: 42px;
  padding: 0 16px;
}

.user-primary-action.is-cancel {
  background: #263238;
}

.user-highlight-card {
  align-items: center;
  background: #eef7f6;
  border-color: #c9e5e1;
  display: grid;
  gap: 16px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  padding: 16px 18px;
}

.user-avatar {
  border-radius: 50%;
  flex: 0 0 48px;
  height: 48px;
  overflow: hidden;
  width: 48px;
}

.user-avatar img {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.user-card-grid {
  display: grid;
  gap: 14px;
}

.user-account-card {
  border-color: #d3dde1;
  align-items: start;
  display: grid;
  gap: 14px;
  grid-template-columns: 48px minmax(0, 1fr) auto;
  padding: 16px 18px;
}

.user-account-card.is-d {
  background: #fffbfb;
  border-color: #f1d6d8;
}

.user-account-card.is-p {
  background: #fffdf7;
}

.user-card-index {
  align-items: center;
  background: #f5f8f9;
  border: 1px solid #dce5e8;
  border-radius: 50%;
  color: #546e7a;
  display: flex;
  font-weight: 800;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.user-card-main {
  min-width: 0;
}

.user-card-head {
  align-items: center;
  display: flex;
  gap: 14px;
  margin-bottom: 12px;
}

.user-meta-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto;
}

.user-meta-grid small,
.user-meta-grid strong {
  display: block;
}

.user-meta-grid small {
  color: #78909c;
  font-size: 0.76rem;
  font-weight: 800;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.user-meta-grid strong {
  color: #263238;
  font-size: 0.94rem;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.user-status-pill,
.user-password-note {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 7px 11px;
}

.user-status-pill.is-a {
  background: #ffffff;
  border: 1px solid #b2dfdb;
  color: #00695c;
}

.user-status-pill.is-p {
  background: #ffffff;
  border: 1px solid #ffe082;
  color: #8a5a00;
}

.user-status-pill.is-d {
  background: #ffffff;
  border: 1px solid #ffcdd2;
  color: #b71c1c;
}

.user-password-note {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #5d4037;
  margin-top: 12px;
}

.user-password-note strong {
  margin-left: 6px;
}

.user-access-summary {
  align-items: center;
  color: #78909c;
  display: flex;
  flex-wrap: wrap;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 12px;
  margin-top: 8px;
}

.user-expand-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 4px;
  margin-top: 10px;
  padding: 0;
}

.user-expand-toggle i {
  font-size: 20px;
}

.user-expand-toggle:hover {
  color: #00897b;
}

.user-detail-drawer {
  background: #fbfcfc;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 13px;
  padding: 14px;
}

.user-detail-drawer small,
.user-detail-drawer strong {
  display: block;
}

.user-detail-drawer small {
  color: #78909c;
  font-size: 0.74rem;
  font-weight: 800;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.user-detail-drawer strong {
  color: #263238;
  font-size: 0.92rem;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.user-detail-wide {
  grid-column: 1 / -1;
}

.user-detail-wide p {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 0;
}

.user-detail-chip,
.user-detail-muted {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 6px 10px;
}

.user-detail-chip {
  background: #ffffff;
  border: 1px solid #dce5e8;
  color: #546e7a;
}

.user-detail-muted {
  background: #ffffff;
  border: 1px dashed #dce5e8;
  color: #78909c;
}

.user-card-actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

.user-card-actions button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #607d8b;
  cursor: pointer;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.user-card-actions button:hover {
  background: #f5f8f9;
  color: #00897b;
}

.user-card-actions button:nth-child(2):hover {
  color: #c62828;
}

.user-empty-card {
  align-items: center;
  display: flex;
  gap: 16px;
  padding: 24px;
}

.user-empty-card i {
  align-items: center;
  background: #eef4f5;
  border-radius: 6px;
  color: #00897b;
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.user-footer-action,
.user-form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 18px;
}

.user-footer-action .btn-large,
.user-form-actions .btn-large {
  background: #00897b;
  border-radius: 6px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: none;
}

.user-editor-form {
  display: grid;
  gap: 18px;
}

.user-editor-section {
  overflow: hidden;
}

.user-editor-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 22px 24px;
}

.user-field-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px;
}

.user-field-wide {
  grid-column: 1 / -1;
}

.user-field-card,
.user-check-card {
  background: #ffffff;
  border: 1px solid #e3e8eb;
  border-radius: 6px;
}

.user-field-card {
  padding: 8px 16px 2px;
}

.user-field-card :deep(.input-field) {
  margin-bottom: 0.6rem;
}

.user-check-card {
  padding: 18px;
}

.user-check-card h6 {
  color: #263238;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0 0 13px;
}

.user-check-card label {
  display: block;
  margin-bottom: 12px;
}

.user-check-card span {
  color: #37474f;
  font-weight: 700;
}

.user-check-card em {
  color: #78909c;
  display: block;
  font-size: 0.82rem;
  font-style: normal;
  font-weight: 600;
  margin-top: 2px;
}

.user-form-error {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  font-weight: 700;
  padding: 14px 16px;
}

.user-form-error strong {
  display: block;
  font-size: 0.94rem;
  font-weight: 800;
  margin-bottom: 6px;
}

.user-form-error ul {
  margin: 0;
  padding-left: 18px;
}

.user-form-error li {
  line-height: 1.45;
  margin: 3px 0;
}

.user-form-actions {
  align-items: center;
  gap: 12px;
}

.user-form-actions .btn-flat {
  color: #607d8b;
  font-weight: 800;
  text-transform: none;
}

@media only screen and (max-width: 992px) {
  .user-toolbar,
  .user-highlight-card,
  .user-account-card {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }

  .user-toolbar {
    flex-direction: column;
  }

  .user-meta-grid,
  .user-field-grid,
  .user-detail-drawer {
    grid-template-columns: 1fr;
  }

  .user-card-actions {
    justify-content: flex-start;
  }
}
</style>
